import { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { InputText, FilterList } from '@teo/components';
import { Search } from '@teo/components/icons';
import { SearchResult } from './SearchResult';
import { AuthContext } from '@/components/Contexts/AuthContext';

export interface Filter {
  label: string;
  value: string;
}

const SearchComponenet = ({
  isMobile,
  activeSearch,
  setActiveSearch,
  collapsed = true,
}: any) => {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const authUser: any = auth?.user;
  const [tagFilter, setTagFilter] = useState<any>([]);
  const [searchText, setSearchText] = useState('');
  const [selected, setSelected] = useState<Filter[]>([]);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const [filter, setFilter] = useState('');
  const [filterType, setFilterType] = useState<any>(null);
  const [filterCourseType, setFilterCourseType] = useState(null);
  const [filterSubType, setFilterSubType] = useState<any>(null);

  const activeOrganisationId = authUser?.activeOrganisationId;
  const userId = authUser?.id;
  const organisationRole = authUser?.activeOrganisationRole;

  const courseType = authUser?.useCourse;
  const assessmentType = authUser?.useAssessment;
  const vacancyType = authUser?.useLeervacature;
  const instructionType = authUser?.useInstruction;
  const lpType = authUser?.useLp;

  const FILTERS = [
    {
      label: t('links.users'),
      value: 'member',
    },
    {
      label: t('links.teams'),
      value: 'team',
    },
    {
      label: t('links.courses'),
      value: 'course',
    },
    {
      label: t('links.assessments'),
      value: 'assessment',
    },
    {
      label: t('links.vacancies'),
      value: 'leervacature',
    },
    {
      label: t('links.learn_paths'),
      value: 'learningpath',
    },
  ];

  useEffect(() => {
    if (authUser?.id) {
      setFilterType(
        authUser?.activeOrganisationRole == 45
          ? 'course,OR,type=coursecontent,OR,type=learningpath,OR,type=learningpathcontent'
          : authUser?.activeOrganisationRole < 21
          ? 'course,OR,type=learningpath'
          : null
      );
      setFilterSubType(
        authUser?.activeOrganisationRole == 45
          ? 'course,OR,subtype=coursecontent_create,OR,subtype=learningpath,OR,subtype=learningpath_create'
          : authUser?.activeOrganisationRole < 21
          ? 'course,OR,subtype=learningpath'
          : null
      );

      const newTagFilter = FILTERS?.map((el) => {
        if (!courseType && el?.value === 'course') return null;
        if (!assessmentType && el?.value === 'assessment') return null;
        if (!vacancyType && el?.value === 'leervacature') return null;
        if (!instructionType && el?.value === 'instruction') return null;
        if (!lpType && el?.value === 'learningpath') return null;
        return el;
      })?.filter((el: any) => el && el);

      if (
        authUser?.activeOrganisationRole !== 45 &&
        authUser?.activeOrganisationRole !== 20 &&
        authUser?.activeOrganisationRole !== 10 &&
        authUser?.activeOrganisationRole != null
      ) {
        setTagFilter(newTagFilter);
      } else {
        const updatedFilterTag = newTagFilter?.filter(
          (item: any) => item.value !== 'member' && item.value !== 'team'
        );
        setTagFilter(updatedFilterTag);
      }
    }
  }, []);

  const handleSearch = (value: any) => {
    setSearchText(value);
  };

  const handleFocusSearch = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setActiveSearch(true);
    document.body.style.overflow = 'hidden';
  };
  const handleBlurSearch = (event: any) => {
    if (searchRef.current && !searchRef.current.contains(event.target)) {
      setActiveSearch(false);
    }
  };

  const handleKeyPress = (event: any) => {
    if (event.key === 'Tab') {
      setActiveSearch(false);
      document.body.style.overflow = 'unset';
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleBlurSearch);
    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('click', handleBlurSearch);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setFilter(searchText), 250);
    return () => clearTimeout(t);
  }, [searchText]);

  useEffect(() => {
    if (!activeSearch) {
      document.body.style.overflow = 'unset';
    }
  }, [activeSearch]);

  useEffect(() => {
    if (selected?.length > 0) {
      if (
        authUser?.activeOrganisationRole !== 45 &&
        authUser?.activeOrganisationRole !== 20 &&
        authUser?.activeOrganisationRole !== 10 &&
        authUser?.activeOrganisationRole != null
      ) {
        let isCourse = false;
        const selectedType: any = selected
          ?.map((el: any) => {
            if (
              el.value === 'course' ||
              el.value === 'assessment' ||
              el.value === 'leervacature' ||
              el.value === 'instruction'
            ) {
              if (isCourse) return null;
              isCourse = true;
              return 'course,OR,type=coursecontent';
            } else if (el.value === 'learningpath') {
              return 'learningpath,OR,type=learningpathcontent';
            } else {
              return el.value;
            }
          })
          ?.filter((el: any) => el && el)
          ?.join(',OR,type=');

        setFilterType(selectedType);
      }

      let isNotCourse = false;
      const selectedCourseType: any = selected
        ?.map((el: any) => {
          if (
            el.value === 'member' ||
            el.value === 'team' ||
            el.value === 'learningpath'
          ) {
            if (isNotCourse) return null;
            isNotCourse = true;
            return 'null';
          } else {
            return el.value;
          }
        })
        ?.filter((el: any) => el && el)
        ?.join(',OR,courseType=');

      setFilterCourseType(selectedCourseType);
    } else {
      setFilterType(null);
      setFilterCourseType(null);
    }
  }, [selected]);

  return (
    <>
      {!(isMobile && collapsed) && (
        <div
          className={`${
            activeSearch && !isMobile
              ? 'absolute left-0 top-3 z-[102] flex w-full'
              : isMobile
              ? 'relative z-20 -mt-1 border-b border-solid border-grey-02 bg-white pb-4'
              : 'relative z-20 ml-2 mr-5 flex-auto lg:ml-4 lg:mr-6'
          }`}
        >
          <div
            onClick={() => handleFocusSearch()}
            ref={searchRef}
            className={`${
              isMobile
                ? 'w-full px-4'
                : 'relative mx-auto w-full max-w-2xl bg-white p-2'
            } ${activeSearch && 'rounded-lg drop-shadow-m'}`}
          >
            <InputText
              Icon={Search}
              id="teo_global_search"
              onChange={(ev: any) => {
                handleSearch(ev.target.value);
              }}
              placeholder={t('global_search.search_placeholder')}
              aria-label={t('global_search.search_placeholder')}
              value={searchText}
              className={`${
                !activeSearch &&
                !isMobile &&
                '!absolute top-1/2 left-0 w-full -translate-y-1/2'
              }`}
              inputClassName={`${!activeSearch && !isMobile && 'pr-2 lg:pr-3'}`}
            />
            {activeSearch && (
              <div
                className={`${
                  isMobile &&
                  'absolute left-0 h-[calc(100vh_-_116px)] w-full bg-white px-2'
                }`}
              >
                <div className="px-2 py-4">
                  <h4 className="mb-2">{t('global_search.search_for')}:</h4>
                  <FilterList
                    filters={tagFilter}
                    onSelect={setSelected}
                    selected={selected}
                  />
                </div>

                <div className="z-1 relative top-px -ml-2 h-px w-[calc(100%_+_16px)] bg-[#EAEAED]"></div>

                {authUser?.activeOrganisationRole > 10 ? (
                  <div className="custom-scrollbar relative -mx-2 max-h-[calc(100vh_-_245px)] overflow-y-auto sm:max-h-[calc(100vh_-_180px)]">
                    <SearchResult
                      authUser={authUser}
                      filter={filter}
                      searchParams={{
                        organisationId: `${activeOrganisationId},OR,organisationId=null`,
                        userId,
                        maxrole: organisationRole,
                        courseType: filterCourseType,
                        type: filterType,
                        subtype: filterSubType,
                      }}
                      setActiveSearch={setActiveSearch}
                      organisationRole={organisationRole}
                      searchText={searchText}
                    />
                  </div>
                ) : (
                  <div className="flex min-h-24 flex-col">
                    <div className="my-8 flex flex-col p-4 text-center">
                      <img
                        className="m-auto w-48"
                        src="/images/components/notifications/theo-empty-inbox.svg"
                      />
                      <p className="pt-4 text-sm text-grey-06">
                        {t('global_search.empty_result')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export { SearchComponenet };
